import { useParams, Link, Navigate } from "react-router-dom";
import { useResource } from "hooks/useResource";
import { getInstitutions, getNotices, getDepartments } from "services/content";
import { getLiveAdmissionRounds } from "services/admissions";
import { mediaUrl } from "lib/media";
import { useSlideshow, layerClass, resolveSlideText, slideShowsText, shadeCss } from "lib/slider";
import SlideImage from "components/shared/SlideImage";
import SectionLabel from "components/shared/SectionLabel";
import Reveal from "components/shared/Reveal";
import Skeleton from "components/shared/Skeleton";
import TeacherGrid from "components/institution/TeacherGrid";

/**
 * One public page per institution, at /institutions/:slug.
 *
 * Everything on the page comes from the `institutions` content blob
 * (admin → Institutions), so adding or renaming a wing needs no code change.
 * Notices are filtered by `noticeTag`; notices with no tags are treated as
 * site-wide and appear on every institution page.
 */
export default function InstitutionPage() {
  const { slug } = useParams();
  const { data, loading } = useResource(getInstitutions);
  const { data: allNotices } = useResource(getNotices);
  const { data: admissionRounds } = useResource(getLiveAdmissionRounds);
  const { data: deptData } = useResource(getDepartments);

  const items = data?.items || [];
  const inst = items.find((i) => i.slug === slug);

  if (loading) {
    return (
      <div className="animate-fadeIn">
        <Skeleton className="h-[380px] rounded-none" />
        <div className="max-w-container mx-auto px-4 sm:px-10 py-12 grid gap-5">
          <Skeleton className="h-[220px] rounded-2xl" />
        </div>
      </div>
    );
  }

  // Unknown slug — send people somewhere useful instead of a blank screen.
  if (!inst) return <Navigate to="/" replace />;

  const admissionRound = admissionRounds?.find(r => r.institution === slug);
  const admissionOpen = admissionRound && admissionRound.acceptingApplications !== false && (!admissionRound.opensAt || new Date(admissionRound.opensAt) <= new Date()) && (!admissionRound.closesAt || new Date(admissionRound.closesAt) >= new Date());
  const color = inst.color || "#005C40";
  const departments = deptData?.items || [];
  const tag = inst.noticeTag || inst.short;
  const notices = (allNotices || [])
    .filter((n) => !n.tags?.length || n.tags.includes(tag))
    .slice(0, 5);

  return (
    <div className="animate-fadeIn">
      <InstitutionHero inst={inst} color={color} />

      {/* Stats strip */}
      {inst.stats?.length > 0 && (
        <div className="bg-green-dark px-4 sm:px-10 py-7">
          <div className="max-w-container mx-auto grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            {inst.stats.map((s, i) => (
              <div key={`${s.label}-${i}`}>
                <div className="font-serif text-[30px] font-bold text-gold-light leading-none">
                  {s.value}
                </div>
                <div className="text-white/65 text-[12.5px] mt-1.5">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      <section className="max-w-container mx-auto px-4 sm:px-10 py-14">
        <div className="grid grid-cols-1 lg:grid-cols-[1.6fr_1fr] gap-8">
          {/* Overview + feature cards */}
          <div>
            <SectionLabel color={color} label="Overview" />
            <h2 className="font-serif text-[28px] font-bold text-green-dark mb-3">
              {inst.overviewTitle || inst.name}
            </h2>
            {inst.overviewDesc && (
              <p className="text-muted text-[14.5px] leading-[1.8] mb-6 max-w-[620px]">
                {inst.overviewDesc}
              </p>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
              {(inst.features || []).map((f, i) => (
                <div
                  key={`${f.title}-${i}`}
                  className="bg-white border border-line rounded-xl px-4 py-3.5 shadow-[0_2px_8px_rgba(0,0,0,0.04)]"
                >
                  <div className="font-bold text-[13.5px] mb-1" style={{ color }}>
                    {f.title}
                  </div>
                  <p className="text-[12.5px] text-muted leading-[1.7]">{f.desc}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Admission card */}
          <div className="flex flex-col gap-4">
            {inst.admission?.title && (
              <div className="rounded-2xl p-6 text-white" style={{ background: color }}>
                {inst.admission.label && (
                  <div className="text-[10.5px] font-bold uppercase tracking-[0.14em] text-white/70 mb-2">
                    {inst.admission.label}
                  </div>
                )}
                <div className="font-serif text-[19px] font-bold mb-2 leading-snug">
                  {admissionRound ? `Admissions ${admissionRound.academicYear}` : "Admissions"}
                </div>
                <p className="text-white/75 text-[13px] leading-[1.7] mb-4">
                  {admissionOpen ? "Applications are open. View the available classes and apply online." : "View admission information or continue an existing application."}
                </p>
                <div className="flex gap-2.5 flex-wrap">
                  {inst.admission.primaryLabel && (
                    <CtaLink
                      href={!inst.admission.primaryHref || inst.admission.primaryHref === "/admissions" ? `/admissions/${slug}` : inst.admission.primaryHref}
                      className="bg-white no-underline rounded-lg px-4 py-2 font-sans font-bold text-[13px]"
                      style={{ color }}
                    >
                      {inst.admission.primaryLabel}
                    </CtaLink>
                  )}
                  {inst.admission.secondaryLabel && (
                    <CtaLink
                      href={inst.admission.secondaryHref}
                      className="bg-white/15 border border-white/30 text-white no-underline rounded-lg px-4 py-2 font-sans font-semibold text-[13px]"
                    >
                      {inst.admission.secondaryLabel}
                    </CtaLink>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Curriculum / programmes */}
      {inst.groups?.length > 0 && (
        <section className="max-w-container mx-auto px-4 sm:px-10 pb-14">
          <SectionLabel color={color} label={inst.curriculumLabel || "Curriculum"} />
          <h2 className="font-serif text-[28px] font-bold text-green-dark mb-7">
            {inst.curriculumTitle || "Stages & Subjects"}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {inst.groups.map((g, i) => (
              <GroupCard key={`${g.name}-${i}`} group={g} fallbackColor={color} departments={departments} />
            ))}
          </div>
          {inst.curriculumCtaLabel && inst.curriculumCtaHref && (
            <Link
              to={inst.curriculumCtaHref}
              className="inline-flex items-center gap-2 mt-6 rounded-lg border-[1.5px] px-4 py-2.5 text-[13px] font-sans font-semibold no-underline"
              style={{ borderColor: `${color}66`, color }}
            >
              {inst.curriculumCtaLabel} <span aria-hidden="true">→</span>
            </Link>
          )}
        </section>
      )}

      {/* Teaching staff for this institution. Renders nothing when the
          school has not listed anyone for it yet. */}
      <TeacherGrid institution={slug} color={color} />

      {/* Notices */}
      {notices.length > 0 && (
        <section className="max-w-container mx-auto px-4 sm:px-10 pb-16">
          <div className="flex items-end justify-between gap-4 mb-5 flex-wrap">
            <div>
              <SectionLabel color={color} label="Latest Updates" />
              <h2 className="font-serif text-[26px] font-bold text-green-dark">
                Notices for {inst.short || inst.name}
              </h2>
            </div>
            <Link
              to={`/notices?tag=${encodeURIComponent(tag)}`}
              className="rounded-lg border-[1.5px] px-4 py-2 text-[12.5px] font-sans font-semibold no-underline shrink-0"
              style={{ borderColor: `${color}66`, color }}
            >
              All Notices
            </Link>
          </div>
          <div className="flex flex-col gap-2.5">
            {notices.map((n, i) => (
              <Reveal key={`${n.text}-${i}`} delay={(i % 4) * 60}>
                <div className="bg-white border border-line rounded-xl px-4 py-3.5 flex items-center gap-4 shadow-[0_2px_8px_rgba(0,0,0,0.03)]">
                  <div className="text-center shrink-0 w-11">
                    <div className="font-serif text-[17px] font-bold leading-none" style={{ color }}>
                      {(n.date || "").split(" ")[1]?.replace(",", "") || "—"}
                    </div>
                    <div className="text-[10px] uppercase text-muted tracking-wider mt-0.5">
                      {(n.date || "").split(" ")[0]}
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[13.5px] text-ink leading-snug">{n.text}</div>
                    {n.tags?.length > 0 && (
                      <div className="flex gap-1.5 mt-1.5 flex-wrap">
                        {n.tags.map((t) => (
                          <span
                            key={t}
                            className="text-[10px] font-bold uppercase tracking-wider rounded px-1.5 py-0.5"
                            style={{ background: `${color}14`, color }}
                          >
                            {t}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  {n.file && (
                    <a
                      href={mediaUrl(n.file)}
                      target="_blank"
                      rel="noreferrer"
                      className="text-[11.5px] font-semibold no-underline rounded-md border px-2.5 py-1.5 shrink-0"
                      style={{ borderColor: `${color}55`, color }}
                    >
                      ↓ PDF
                    </a>
                  )}
                </div>
              </Reveal>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

/** Internal paths route through react-router; anything else is a plain link. */
function CtaLink({ href, children, className, style }) {
  if (!href) return null;
  return href.startsWith("/") ? (
    <Link to={href} className={className} style={style}>
      {children}
    </Link>
  ) : (
    <a href={href} target="_blank" rel="noreferrer" className={className} style={style}>
      {children}
    </a>
  );
}

function GroupCard({ group, fallbackColor, departments = [] }) {
  const color = group.color || fallbackColor;

  // "plain" = a titled card with a blurb (honours programmes), no subject list.
  // When the card's name is a department code (FNS / CDSR / FD) it becomes a
  // link to that department's own page.
  if (group.variant === "plain") {
    const key = String(group.name || "").trim().toLowerCase();
    const dept = departments.find(
      (d) => d.code?.toLowerCase() === key || d.slug === key || d.name?.toLowerCase() === key
    );
    const inner = (
      <>
        <div className="flex items-start justify-between gap-3">
          <div className="font-serif text-[19px] font-bold mb-1.5" style={{ color }}>
            {group.name}
          </div>
          {dept && (
            <span className="text-[18px] leading-none mt-0.5 transition-transform group-hover:translate-x-1" style={{ color }} aria-hidden="true">
              →
            </span>
          )}
        </div>
        <p className="text-[12.5px] text-muted leading-[1.7]">{group.desc}</p>
        {dept && (
          <div className="text-[12px] font-semibold mt-3" style={{ color }}>
            {dept.name} · open department page
          </div>
        )}
      </>
    );
    const cls =
      "block bg-white border border-line rounded-2xl px-5 py-5 shadow-[0_2px_10px_rgba(0,0,0,0.04)] no-underline transition-all duration-300";
    return dept ? (
      <Link
        to={`/departments/${dept.slug}`}
        title={`${dept.name} — open department page`}
        className={`${cls} group cursor-pointer hover:-translate-y-1 hover:shadow-[0_12px_28px_rgba(0,0,0,0.10)]`}
        style={{ borderColor: `${color}55` }}
      >
        {inner}
      </Link>
    ) : (
      <div className={cls}>{inner}</div>
    );
  }

  return (
    <div className="bg-white border border-line rounded-2xl overflow-hidden shadow-[0_4px_18px_rgba(0,0,0,0.06)] transition-transform duration-300 hover:-translate-y-1">
      <div className="px-5 py-[18px] flex items-center gap-3" style={{ background: color }}>
        {group.icon && <span className="text-[26px]">{group.icon}</span>}
        <div className="font-serif text-[18px] font-bold text-white">{group.name}</div>
      </div>
      <div className="px-5 py-4">
        <div className="font-semibold text-[11px] text-muted tracking-[0.08em] uppercase mb-2.5">
          Subjects
        </div>
        <div className="flex flex-col gap-1.5">
          {(group.subs || []).map((s) => (
            <div key={s} className="flex items-center gap-2.5 text-[13px]">
              <span
                className="w-[6px] h-[6px] rounded-full shrink-0"
                style={{ background: color }}
              />
              {s}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/** Hero with the institution's own slides; dots only appear with 2+ slides. */
function InstitutionHero({ inst, color }) {
  const slides = inst.slides?.length
    ? inst.slides
    : [{ headline: inst.name, sub: "", image: inst.heroImage || null }];
  // Shared slideshow engine (lib/slider.js): per-slide animation, duration,
  // "show text" and "same text as previous slide" — the same options the
  // homepage hero has, edited under admin → Institutions → Hero slides.
  const show = useSlideshow(slides, { defaultDuration: 6 });
  const { cur } = show;

  const s = slides[cur];
  const { text, key: textKey } = resolveSlideText(slides, cur);
  const hasText = slideShowsText(s) && (text.headline || text.sub);
  const base = inst.heroBg || `linear-gradient(140deg,#003d2b,${color})`;
  // Shade is per slide now (admin -> Hero slides -> Photo shade) and defaults
  // to none, so photos show clean unless an editor asks for contrast.
  const shade = shadeCss(s?.shade);

  return (
    <section
      /* Fixed height: slides carry different amounts of text, and letting the
         banner size to its content made it jump on every rotation. */
      className="relative overflow-hidden px-4 sm:px-10 py-12 min-h-[340px] sm:min-h-[400px] lg:min-h-[460px] flex items-center"
      style={{ background: base }}
      aria-roledescription="carousel"
    >
      {/* Background slider: one stacked layer per slide, animated by the
          .sl-* CSS. Slides without a photo simply show the gradient, so this
          degrades to the previous look when no images are uploaded. */}
      {slides.map((sl, i) => {
        if (!sl.image && !sl.mobileImage) return null;
        return (
          <div
            key={`${sl.image}-${sl.mobileImage}-${i}`}
            aria-hidden="true"
            className={layerClass(i, show, sl.animation, "pointer-events-none")}
            style={{ "--sl-dir": show.dir }}
          >
            {/* Desktop photo, swapped for the mobile upload under 768px. */}
            <SlideImage slide={sl} />
          </div>
        );
      })}

      {/* Readability shade — off by default, chosen per slide in admin. */}
      {shade && (
        <div aria-hidden="true" className="absolute inset-0 pointer-events-none z-[1]" style={{ background: shade }} />
      )}


      <div className="max-w-container mx-auto w-full relative z-[2]">
        <div className="text-[12px] text-white/55 mb-4">
          <Link to="/" className="text-white/55 no-underline hover:text-white">
            Home
          </Link>
          <span className="mx-1.5">›</span>
          <span className="text-white/80">{inst.name}</span>
        </div>

        <div className="max-w-[680px]">
          {/* The institution's own name always leads; the slide headline
              becomes the tagline beneath it (hidden per slide via "show text"). */}
          <h1 className="font-serif font-extrabold text-white leading-[1.12] mb-2.5 text-[clamp(32px,4.6vw,54px)]">
            {inst.name}
          </h1>
          {hasText && (
            <div key={textKey} className="sl-cap">
              {text.headline && (
                <div className="font-serif text-[clamp(18px,2vw,24px)] text-white/85 leading-snug whitespace-pre-line mb-3">
                  {text.headline}
                </div>
              )}
              {text.sub && <p className="text-white/75 text-[14.5px] leading-[1.75]">{text.sub}</p>}
            </div>
          )}
        </div>

        {slides.length > 1 && (
          <div className="flex gap-2 mt-7">
            {slides.map((_, i) => (
              <button
                key={i}
                onClick={() => show.go(i)}
                aria-label={`Go to slide ${i + 1}`}
                aria-current={i === cur ? "true" : undefined}
                className="h-[7px] rounded-full border-none cursor-pointer transition-all duration-300 p-0"
                style={{
                  width: i === cur ? 26 : 7,
                  background: i === cur ? "#fff" : "rgba(255,255,255,0.35)",
                }}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
